import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  {
    icon: Shield,
    title: "SEO Audits",
    description:
      "Review of your current website structure, technical issues, and search visibility gaps.",
  },
  {
    icon: TrendingUp,
    title: "Keyword Research",
    description:
      "Focused keyword planning based on service intent, relevance, and search opportunity.",
  },
  {
    icon: Layers,
    title: "On-Page SEO",
    description:
      "Optimization of headings, page structure, titles, descriptions, content, and internal linking.",
  },
  {
    icon: Zap,
    title: "Technical SEO",
    description:
      "Improvements to site health, crawlability, speed, indexing, and overall structure.",
  },
  {
    icon: Smartphone,
    title: "Local SEO Support",
    description:
      "SEO improvements that help service businesses appear more clearly in local search results.",
  },
  {
    icon: Code,
    title: "SEO Content Direction",
    description:
      "Guidance on service-page content, supporting content, and page-level keyword targeting.",
  },
];

const offerings = [
  {
    title: "Website SEO Foundations",
    description:
      "SEO improvements for websites that need stronger structure, page clarity, and search readiness.",
  },
  {
    title: "Service Page Optimization",
    description:
      "Page-level improvements that help core service pages target the right searches more clearly.",
  },
  {
    title: "Technical SEO Improvements",
    description:
      "Fixes and improvements that support indexing, speed, usability, and site health.",
  },
  {
    title: "Local Search Visibility",
    description:
      "SEO support for businesses that want to improve how they appear in local and regional searches.",
  },
];

const deliverables = [
  "SEO audit and findings summary",
  "Keyword direction for priority pages",
  "On-page optimization recommendations",
  "Technical SEO improvement list",
  "Internal linking suggestions",
  "Metadata guidance for key pages",
  "Content direction where needed",
  "Clear priorities for next steps",
];

const processSteps = [
  {
    step: "01",
    title: "Audit",
    description:
      "We review your website, structure, current visibility, and the main issues holding search performance back.",
  },
  {
    step: "02",
    title: "Research",
    description:
      "We identify relevant keywords, user intent, page opportunities, and supporting search themes.",
  },
  {
    step: "03",
    title: "Prioritize",
    description:
      "We define which pages, issues, and improvements matter most based on business goals.",
  },
  {
    step: "04",
    title: "Optimize",
    description:
      "We improve structure, on-page elements, internal links, and technical foundations.",
  },
  {
    step: "05",
    title: "Strengthen Content",
    description:
      "Where needed, we improve page copy and content direction to better match search intent.",
  },
  {
    step: "06",
    title: "Monitor & Improve",
    description:
      "SEO is not one-time work. We continue refining based on performance and evolving priorities.",
  },
];

const faqs = [
  {
    question: "What is included in your SEO service?",
    answer: (
      <>
        Our SEO service may include audits, keyword research, on-page SEO,
        technical improvements, internal linking suggestions, and{" "}
        <Link
          to="/services/social-media-marketing"
          className="text-primary hover:underline"
        >
          content direction
        </Link>{" "}
        depending on what your website needs.
      </>
    ),
    schemaAnswer:
      "Our SEO service may include audits, keyword research, on-page SEO, technical improvements, internal linking suggestions, and content direction depending on what your website needs.",
  },
  {
    question: "How long does SEO take to show results?",
    answer: (
      <>
        SEO usually takes time to build momentum. Timelines vary depending on
        competition, website condition, and the scope of work, but it is a
        long-term growth channel.
      </>
    ),
    schemaAnswer:
      "SEO usually takes time to build momentum. Timelines vary depending on competition, website condition, and the scope of work, but it is a long-term growth channel.",
  },
  {
    question: "Do you only do local SEO?",
    answer: (
      <>
        No. We can support local search visibility as well as broader
        service-page and website SEO depending on your goals.
      </>
    ),
    schemaAnswer:
      "No. We can support local search visibility as well as broader service-page and website SEO depending on your goals.",
  },
  {
    question: "Can you improve the SEO of an existing website?",
    answer: (
      <>
        Yes. We can improve site structure, metadata, internal linking, content
        alignment, and{" "}
        <Link
          to="/services/maintenance-support"
          className="text-primary hover:underline"
        >
          technical SEO
        </Link>{" "}
        on an{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          existing website
        </Link>
        .
      </>
    ),
    schemaAnswer:
      "Yes. We can improve site structure, metadata, internal linking, content alignment, and technical SEO on an existing website.",
  },
  {
    question: "Do you write SEO content too?",
    answer: (
      <>
        We provide content direction and page-level recommendations. Where
        needed, we can also support{" "}
        <Link
          to="/services/social-media-marketing"
          className="text-primary hover:underline"
        >
          content improvements
        </Link>{" "}
        that better match search intent.
      </>
    ),
    schemaAnswer:
      "We provide content direction and page-level recommendations. Where needed, we can also support content improvements that better match search intent.",
  },
  {
    question: "Does SEO work better with a strong website?",
    answer: (
      <>
        Yes. SEO works best when the{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          website structure, speed, usability
        </Link>
        , and content all support the same goal.
      </>
    ),
    schemaAnswer:
      "Yes. SEO works best when the website structure, speed, usability, and content all support the same goal.",
  },
];

const whyChooseUs = [
  {
    title: "Strategy Before Tactics",
    description:
      "We define what needs to improve before making changes, so the SEO work has direction.",
  },
  {
    title: "Built Around Real Search Intent",
    description:
      "We focus on the keywords, pages, and structure that match how your ideal client actually searches.",
  },
  {
    title: "Technical + Content Thinking",
    description:
      "SEO works best when site performance, structure, and content all support the same goal.",
  },
  {
    title: "Practical Growth Focus",
    description:
      "The goal is not just more traffic. The goal is better visibility that supports real business growth.",
  },
];

export default function WebsiteDevelopmentPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      <SEO
  title="Best Search Engine Optimization Service | Square Trigger"
  description="Get Affordable SEO services in Canada that help improve your Google ranking and drive more traffic and revenue. Start ranking higher today."
  ogTitle="Best Search Engine Optimization Service | Square Trigger"
  ogDescription="Get Affordable SEO services in Canada that help improve your Google ranking and drive more traffic and revenue. Start ranking higher today."
  ogSiteName="Square Trigger SEO services"
  ogImage="https://squaretrigger.com/seo.webp"
  keywords={SEO_KEYWORDS.seoServices}
/>

      {/* Hero Section */}
<section className="relative overflow-hidden">
  
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
    style={{ backgroundImage: "url('/seo.webp')" }}
  />

  {/* Overlay for mobile + desktop */}
  <div className="absolute inset-0 bg-gradient-to-r from-foreground/72 via-foreground/58 to-foreground/38 lg:bg-gradient-to-br lg:from-foreground lg:via-foreground/95 lg:to-foreground/90" />

  <div className="absolute inset-0">
          <div className="absolute top-16 right-6 h-72 w-72 rounded-full bg-primary/20 blur-3xl sm:top-20 sm:right-20 sm:h-96 sm:w-96" />
          <div className="absolute bottom-16 left-6 h-56 w-56 rounded-full bg-secondary/20 blur-3xl sm:bottom-20 sm:left-20 sm:h-72 sm:w-72" />
        </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
              >
                <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">Our Services</span>
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className='mx-auto m-0 mb-[18px] max-w-[760px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
              >
                Search Engine Optimization Services That Help the Right Clients{" "}
                <span className="gradient-text">Find You</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mx-auto m-0 mb-3 max-w-[640px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We provide SEO services that improve visibility, strengthen search
                performance, and help your business get found by people already
                looking for what you offer.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mx-auto m-0 mb-6 max-w-[640px] text-[0.95rem] italic text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We focus on strategy, technical improvements, and content direction
                so your website can attract better traffic and stronger business
                opportunities over time.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
              >
                <Button
                  variant="gradient"
                  asChild
                  className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  asChild
                  className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link
                    to="/work"
                    className="flex items-center justify-center gap-2"
                  >
                    View Our Work
                  </Link>
                </Button>
              </motion.div>
            </div>

            <div className="hidden min-h-[420px] items-center justify-center lg:flex">
  <motion.div
    initial={{ opacity: 0, x: 30 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.8, delay: 0.4 }}
    className="relative flex items-center justify-center"
  >
   
    <div className="relative group">
      <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
      <img 
        src="/seo.webp" 
        alt="Digital Agency Vancouver" 
        className="relative rounded-2xl shadow-2xl border border-white/10 object-cover w-full max-w-[520px] h-auto"
      />
    </div>
  </motion.div>
</div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="mb-4 text-3xl font-display font-bold text-foreground md:text-4xl">
              Get Found When People Are Already{" "}
              <span className="gradient-text">Searching</span>
            </h2>

            <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              SEO helps your business appear when potential clients are actively
              looking for your services. It is not just about rankings. It is
              about showing up at the right time, for the right search, with the
              right page.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card/60 p-6 shadow-soft backdrop-blur sm:p-8"
            >
              <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                A strong SEO strategy improves visibility, supports trust, and
                helps bring in traffic that is more likely to convert.
              </p>

              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-5">
                {[
                  {
                    title: "Better visibility",
                    desc: "Appear more often in search results for the services your business actually offers.",
                  },
                  {
                    title: "Qualified traffic",
                    desc: "SEO helps bring in people who are already searching with intent.",
                  },
                  {
                    title: "Long-term value",
                    desc: "Unlike short-term campaigns, SEO builds stronger visibility over time.",
                  },
                  {
                    title: "Stronger trust",
                    desc: "People trust businesses they can find clearly and consistently online.",
                  },
                  {
                    title: "Better digital foundation",
                    desc: "SEO works best when your website structure, content, and performance all support it.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-center transition-shadow hover:shadow-medium"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                      {item.title === "Better digital foundation" ? (
                        <>
                          SEO works best when your{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            website structure
                          </Link>
                          ,{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>
                          , and{" "}
                          <Link
                            to="/services/maintenance-support"
                            className="text-primary hover:underline"
                          >
                            performance
                          </Link>{" "}
                          all support it.
                        </>
                      ) : (
                        item.desc
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-14 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <h2 className="text-3xl font-display font-bold text-foreground md:text-4xl">
              Why Choose Square Trigger for SEO Services
            </h2>
            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              We do not treat SEO like a checklist. We look at how your website
              is built, how your pages are structured, what your audience is
              searching for, and what is stopping the business from being found
              more effectively.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="flex flex-col items-center rounded-2xl border border-border bg-card p-6 text-center shadow-soft transition-shadow hover:shadow-medium sm:items-start sm:text-left"
              >
                <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-xl gradient-primary-soft sm:mx-0">
                  <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                </div>

                <h3 className="mb-2 font-semibold text-foreground">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {item.title === "Technical + Content Thinking" ? (
                    <>
                      SEO works best when{" "}
                      <Link
                        to="/services/maintenance-support"
                        className="text-primary hover:underline"
                      >
                        site performance
                      </Link>
                      ,{" "}
                      <Link
                        to="/services/website-development"
                        className="text-primary hover:underline"
                      >
                        structure
                      </Link>
                      , and{" "}
                      <Link
                        to="/services/social-media-marketing"
                        className="text-primary hover:underline"
                      >
                        content
                      </Link>{" "}
                      all support the same goal.
                    </>
                  ) : (
                    item.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-14"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Capabilities
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              What Our SEO Services <span className="gradient-text">Include</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-all duration-300 hover:border-primary/30 sm:items-start sm:text-left">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary sm:mx-0">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                      {capability.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {capability.title === "SEO Audits" ? (
                        <>
                          Review of your current website{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            structure
                          </Link>
                          , technical issues, and search visibility gaps.
                        </>
                      ) : capability.title === "On-Page SEO" ? (
                        <>
                          Optimization of headings,{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            page structure
                          </Link>
                          , titles, descriptions,{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>
                          , and internal linking.
                        </>
                      ) : capability.title === "Technical SEO" ? (
                        <>
                          Improvements to{" "}
                          <Link
                            to="/services/maintenance-support"
                            className="text-primary hover:underline"
                          >
                            site health, crawlability, speed
                          </Link>
                          , indexing, and overall structure.
                        </>
                      ) : capability.title === "SEO Content Direction" ? (
                        <>
                          Guidance on service-page{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>
                          , supporting content, and page-level keyword targeting.
                        </>
                      ) : (
                        capability.description
                      )}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding relative overflow-hidden bg-foreground text-background">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-secondary">
              Solutions
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold md:text-4xl">
              SEO Support Built Around <span className="gradient-text">Business Goals</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:gap-8">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="flex flex-col items-center rounded-2xl border border-background/10 bg-background/5 p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:p-8 sm:text-left"
              >
                <h3 className="mb-3 text-xl font-display font-bold sm:text-2xl">
                  {offering.title === "Website SEO Foundations" ? (
                    <Link
                      to="/services/website-development"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : offering.title === "Technical SEO Improvements" ? (
                    <Link
                      to="/services/maintenance-support"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : (
                    offering.title
                  )}
                </h3>

                <p className="leading-relaxed text-background/70">
                  {offering.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium uppercase tracking-wider text-primary">
                Deliverables
              </span>

              <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="mx-auto mb-8 max-w-[720px] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
                Our SEO service is built around what your website actually needs
                to improve visibility and search performance.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full justify-center sm:w-auto"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft sm:p-8 sm:text-left"
            >
              <ul className="inline-grid space-y-4 text-left sm:grid">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex w-fit items-start gap-3"
                  >
                    <CheckCircle className="mt-[2px] h-5 w-5 flex-shrink-0 text-primary" />
                    <span className="text-sm leading-relaxed text-foreground/90 sm:text-base">
                      {item === "Metadata guidance for key pages" ? (
                        <>
                          Metadata guidance for{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            key pages
                          </Link>
                        </>
                      ) : item === "Content direction where needed" ? (
                        <>
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            Content direction
                          </Link>{" "}
                          where needed
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-12 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Our Process
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              How We <span className="gradient-text">Approach SEO</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:text-left">
                  <div className="mb-4 flex w-full justify-center">
                    <div className="text-center text-3xl font-display font-bold gradient-text">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {step.title === "Optimize" ? (
                      <>
                        We improve structure, on-page elements, internal links,
                        and{" "}
                        <Link
                          to="/services/maintenance-support"
                          className="text-primary hover:underline"
                        >
                          technical foundations
                        </Link>
                        .
                      </>
                    ) : step.title === "Strengthen Content" ? (
                      <>
                        Where needed, we improve page copy and{" "}
                        <Link
                          to="/services/social-media-marketing"
                          className="text-primary hover:underline"
                        >
                          content direction
                        </Link>{" "}
                        to better match search intent.
                      </>
                    ) : (
                      step.description
                    )}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-[#7c1fff] via-[#4c4cf4] to-[#25a9f5] px-6 py-14 text-center shadow-soft sm:px-10 sm:py-16 lg:px-16 lg:py-20"
          >
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
              <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-primary-foreground sm:mb-6 md:text-4xl">
                Need SEO That Supports Real Business Growth?
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                Let’s look at what is holding your website back in search and what
                needs to improve first.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="premium"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background text-foreground hover:bg-background/90 sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  size="lg"
                  asChild
                  className="w-full justify-center border border-white/20 bg-white/10 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/15 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-10 text-center sm:mb-12"
            >
              <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
                SEO Services –{" "}
                <span className="gradient-text">Frequently Asked Questions</span>
              </h2>
            </motion.div>

            <div className="mx-auto max-w-[1000px] space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
                >
                  <button
                    type="button"
                    onPointerUp={() =>
                      setOpenFaq(openFaq === index ? null : index)
                    }
                    className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
                  >
                    <span className="pr-6 font-semibold text-foreground">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.schemaAnswer,
                  },
                })),
              })}
            </script>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[28px] border border-border/70 bg-card/40 px-6 py-14 text-center shadow-soft backdrop-blur-sm sm:px-10 sm:py-16 lg:px-16 lg:py-18"
          >
            <div className="mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-foreground sm:mb-6 md:text-4xl">
                Ready to Improve Your Search Visibility?
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Let’s identify what your website needs to rank more clearly,
                attract better traffic, and support stronger growth.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="gradient"
                  size="lg"
                  asChild
                  className="group w-full justify-center sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center"
                  >
                    Contact Us
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}